import React from 'react';
import { Steps } from 'antd';
import { 
  CheckCircleOutlined, 
  ClockCircleOutlined, 
  ExclamationCircleOutlined 
} from '@ant-design/icons';
import { OnboardingStep } from '../types';

interface OnboardingStepIndicatorProps {
  steps: OnboardingStep[];
  currentStep: number;
  onStepClick?: (stepIndex: number) => void;
  direction?: 'horizontal' | 'vertical';
  orientation?: 'horizontal' | 'vertical'; // Alias for direction
  size?: 'default' | 'small';
}

const OnboardingStepIndicator: React.FC<OnboardingStepIndicatorProps> = ({
  steps,
  currentStep,
  onStepClick,
  direction,
  orientation,
  size = 'default',
}) => {
  // Use orientation if provided, otherwise fall back to direction, default to horizontal
  const actualDirection = orientation || direction || 'horizontal';
  const getStepStatus = (step: OnboardingStep, index: number) => {
    if (step.isCompleted) {
      return 'finish';
    }
    if (index === currentStep) {
      return step.validationErrors && step.validationErrors.length > 0 ? 'error' : 'process';
    }
    if (index < currentStep) {
      return 'finish';
    }
    return 'wait';
  };

  const getStepIcon = (step: OnboardingStep, index: number) => {
    if (step.isCompleted) {
      return <CheckCircleOutlined />;
    }
    if (index === currentStep && step.validationErrors && step.validationErrors.length > 0) {
      return <ExclamationCircleOutlined />;
    }
    if (step.isRequired) {
      return <ClockCircleOutlined />;
    }
    return undefined;
  };

  const stepItems = steps.map((step, index) => ({
    title: step.title,
    description: size === 'default' ? step.description : undefined,
    status: getStepStatus(step, index) as 'error' | 'wait' | 'finish' | 'process' | undefined,
    icon: getStepIcon(step, index),
    disabled: !step.isCompleted && index > currentStep + 1,
  }));

  return (
    <div className="onboarding-step-indicator">
      <Steps
        current={currentStep}
        direction={actualDirection}
        size={size}
        items={stepItems}
        onChange={onStepClick}
        className="onboarding-steps"
      />
      
      {size === 'default' && (
        <div className="step-legend">
          <div className="legend-item">
            <CheckCircleOutlined style={{ color: '#52c41a' }} />
            <span>Completed</span>
          </div>
          <div className="legend-item">
            <ClockCircleOutlined style={{ color: '#1890ff' }} />
            <span>Current</span>
          </div>
          <div className="legend-item">
            <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
            <span>Error</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingStepIndicator;
